import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowUpRight } from "lucide-react";

/**
 * Warm, playful welcome hero for founder / investor dashboards.
 *
 * Renders a full-width card with:
 *  – A soft gradient wash of pastel blobs
 *  – A time-of-day-aware greeting with a waving hand
 *  – Optional "chip" chip in the top-right for status/tier badges
 *  – Optional metrics strip under the greeting
 */
export function WelcomeHero({
  name,
  eyebrow = "TODAY AT VENTURAZ",
  subhead = "Here's what's moving on your desk.",
  actions,
  badge,
  children,
}) {
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const first = (name || "there").split(/\s+/)[0];

  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-[24px] liquid-morph-panel border border-parchment mb-6"
      data-testid="welcome-hero"
    >
      {/* Single subtle gradient wash — professional, not carnival */}
      <div className="absolute inset-0 pointer-events-none opacity-70" aria-hidden>
        <div className="absolute -top-32 -right-20 w-96 h-96 rounded-full bg-gold/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-20 w-80 h-80 rounded-full bg-forest/[0.06] blur-3xl" />
      </div>

      <div className="relative p-6 lg:p-9">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.3em] text-navy-deep/60">
              <span className="w-1.5 h-1.5 rounded-full bg-gold" />
              {eyebrow}
            </div>
            <h1 className="mt-3 font-display text-3xl lg:text-4xl xl:text-5xl text-navy-deep leading-[1.05]">
              {greeting}, {first}
            </h1>
            <p className="mt-3 text-sm lg:text-base text-ink/65 max-w-xl leading-relaxed">
              {subhead}
            </p>
          </div>
          {badge && (
            <div className="flex-shrink-0" data-testid="welcome-hero-badge">
              {badge}
            </div>
          )}
        </div>

        {children && (
          <div className="mt-6 lg:mt-7" data-testid="welcome-hero-body">
            {children}
          </div>
        )}

        {actions && (
          <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="welcome-hero-actions">
            {actions}
          </div>
        )}
      </div>
    </motion.section>
  );
}

/**
 * Refined stat tile — subtle, editorial. One quiet accent stripe on the left,
 * generous whitespace, gold icon. Rotates a soft accent hue by index without
 * feeling like a carnival.
 */
const ACCENT_STYLES = {
  gold: "before:bg-gold",
  forest: "before:bg-forest",
  navy: "before:bg-navy",
  ink: "before:bg-ink",
};
const ACCENTS = ["gold", "forest", "navy", "ink"];

export function WarmStatTile({ label, value, icon: Icon, tone, index = 0, testId, hint, href }) {
  const accent = tone || ACCENTS[index % ACCENTS.length];
  const stripe = ACCENT_STYLES[accent] || ACCENT_STYLES.gold;
  const isLink = Boolean(href);
  const Comp = isLink ? Link : "div";
  const props = isLink ? { to: href } : {};
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: 0.04 * (index + 1), ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -2 }}
    >
      <Comp
        {...props}
        className={`group relative overflow-hidden rounded-[16px] border border-parchment bg-white/90 backdrop-blur-sm p-5 shadow-[0_2px_10px_rgba(15,61,46,0.04)] transition-shadow ${isLink ? "hover:shadow-[0_10px_28px_rgba(15,61,46,0.10)] hover:border-gold/40 cursor-pointer block" : "hover:shadow-[0_8px_24px_rgba(15,61,46,0.08)]"} before:content-[''] before:absolute before:left-0 before:top-4 before:bottom-4 before:w-[3px] before:rounded-full ${stripe}`}
        data-testid={testId}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-ink/50">
            {label}
          </div>
          <div className="flex items-center gap-1">
            {Icon && (
              <div className="w-7 h-7 rounded-full bg-gold/10 flex items-center justify-center text-gold">
                <Icon size={13} strokeWidth={2.4} />
              </div>
            )}
            {isLink && (
              <span className="opacity-0 group-hover:opacity-100 transition-opacity text-gold">
                <ArrowUpRight size={14} strokeWidth={2.4} />
              </span>
            )}
          </div>
        </div>
        <div className="font-display text-3xl leading-none text-navy-deep">
          {value}
        </div>
        {hint && <div className="mt-2 text-[11px] text-ink/55">{hint}</div>}
      </Comp>
    </motion.div>
  );
}

/**
 * Wrap a section (Card) with a soft entrance. Use for below-fold blocks so
 * the whole dashboard has a staggered, cohesive rise-in feel.
 */
export function SoftSection({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
